import React from 'react';
import { 
  Zap, 
  ShieldAlert, 
  MapPin, 
  Award, 
  CheckSquare, 
  HelpCircle, 
  Check,
  ChevronDown,
  ChevronUp,
  ShieldCheck,
  Sparkles
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export default function Benefits() {
  const [showAll, setShowAll] = React.useState(false);
  const { t } = useLanguage();

  const benefits = [
    {
      title: t('benefits.b1_title'),
      description: t('benefits.b1_desc'),
      icon: Zap,
    },
    {
      title: t('benefits.b2_title'),
      description: t('benefits.b2_desc'),
      icon: ShieldAlert,
    },
    {
      title: t('benefits.b3_title'),
      description: t('benefits.b3_desc'),
      icon: MapPin,
    },
    {
      title: t('benefits.b4_title'),
      description: t('benefits.b4_desc'),
      icon: Award,
    },
    {
      title: t('benefits.b5_title'),
      description: t('benefits.b5_desc'),
      icon: CheckSquare,
    },
    {
      title: t('benefits.b6_title'),
      description: t('benefits.b6_desc'),
      icon: HelpCircle,
    },
    {
      title: t('benefits.b7_title'),
      description: t('benefits.b7_desc'),
      icon: ShieldCheck,
    },
    {
      title: t('benefits.b8_title'),
      description: t('benefits.b8_desc'),
      icon: Sparkles,
    },
  ];

  const visibleBenefits = showAll ? benefits : benefits.slice(0, 4);

  return (
    <section id="fordele" className="relative py-24 bg-slate-950 border-y border-slate-900 overflow-hidden">
      {/* Background Image with Dark Overlay */}
      <div className="absolute inset-0 z-0">
        <img 
          src="https://assets.auxodev.dk/assets/5bfde40d-44ae-4b1e-9e89-bf8f3817c44f/84db5354-ab4a-4f42-a999-06b15250fa2d.jpg" 
          alt="Premium BMW X5" 
          className="w-full h-full object-cover object-center opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/65 to-slate-950/75" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          {/* Left Column - Text */}
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black italic text-white tracking-tight mb-6 leading-tight uppercase">
              {t('benefits.title')}
            </h2>
            <p className="text-[#f1f1f1] mb-8 leading-relaxed">
              {t('benefits.desc')}
            </p>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="text-white shrink-0 mt-1">
                  <Check className="h-4 w-4" />
                </div>
                <span className="text-sm font-medium text-[#f1f1f1]">{t('benefits.bullet1')}</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="text-white shrink-0 mt-1">
                  <Check className="h-4 w-4" />
                </div>
                <span className="text-sm font-medium text-[#f1f1f1]">{t('benefits.bullet2')}</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="text-white shrink-0 mt-1">
                  <Check className="h-4 w-4" />
                </div>
                <span className="text-sm font-medium text-[#f1f1f1]">{t('benefits.bullet3')}</span>
              </div>
            </div>
          </div>

          {/* Right Column - Benefits Grid */}
          <div className="lg:col-span-7 flex flex-col space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {visibleBenefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div 
                    key={index} 
                    className="bg-white p-6 rounded-none border border-slate-100 border-t-4 border-t-[#76eeda] hover:border-slate-200 hover:border-t-[#76eeda] transition-all duration-300 shadow-sm"
                  >
                    <div className="text-[#293e30] mb-4">
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="text-base font-bold text-slate-900 mb-2 uppercase tracking-wide">{benefit.title}</h3>
                    <p className="text-xs text-slate-600 leading-relaxed">{benefit.description}</p>
                  </div>
                );
              })}
            </div>

            {benefits.length > 4 && (
              <div className="flex justify-center lg:justify-start">
                <button
                  onClick={() => setShowAll(!showAll)}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 text-white font-bold text-xs tracking-wider uppercase rounded-none transition-all cursor-pointer bg-transparent hover:text-slate-200"
                >
                  {showAll ? (
                    <>
                      {t('benefits.less')}
                      <ChevronUp className="h-4 w-4 shrink-0" />
                    </>
                  ) : (
                    <>
                      {t('benefits.more')}
                      <ChevronDown className="h-4 w-4 shrink-0" />
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

        </div>
      </div>
    </section>
  );
}
